import React, {
  useRef,
  useEffect,
  useMemo,
  Fragment,
  forwardRef,
  useState,
} from "react";
import { useServiceStore } from "store";
import {pixelPerInch } from "constants/appConstant";
import Wrapper from "./headwall-components/wrapper";
import { HorizontalRuler, HospitalBed } from "components";
import {
  alterImageUrl,
  getConsoleDropPoints,
} from "config/calculations/calculation";
import { InsightLockIcon } from "assets/images";

const outerClass = {
  surface_mounted: "security_headwall_surface",
  enclosure: "security_headwall_enclousre",
  recessed: "security_headwall_recessed",
  insight: "security_headwall_insight",
};

const innerClass = {
  surface_mounted: "security_headwall_surface_inner",
  enclosure: "security_headwall_enclousre_inner",
  recessed: "security_headwall_recessed_inner",
  insight: "security_headwall_insight_inner",
};

const SecurityConsolePanel = forwardRef((props, ref) => {
  const {
    values,
    ruler,
    handleDragStart,
    handleonDragOver,
    handleDrop,
    handleDragEnter,
    dragEntered,
    wallLength,
    service,
    clickArrowRight,
    clickArrowLeft,
    showBedGraphic,
  } = props;

  const { serv, view_only, setService } = useServiceStore();

  const { headwalls, width, ceiling_height, model, sub_model } = values;
  const [customEvent, setCustomEvent] = useState(null);
  const [railInd, setRailInd] = useState(0);
  const [railDropBoxes, setRailDropBoxes] = useState([]);
  const wrapperRef = useRef(null);

  useEffect(() => {
    //total border area around the panel from both sides in inches
    const borderAreaMapObj = {
      surface_mounted: 7.125,
      enclosure: 6.25,
      recessed: 4.3756,
      insight: 5.375,
    };

    // first point from the starting of the panel edge by subtracting border areas
    const startingPointObj = {
      surface_mounted: 20.0892857143, //1.125
      enclosure: 27.9017857143, //1.5625
      recessed: 29.3809523809, //1.64533333333
      insight: 20.0892857143, //1.125
    };

    const consoleWidth =
      (width.ft * 12 + width.in - borderAreaMapObj[sub_model]) / pixelPerInch;
    const dropPoints = getConsoleDropPoints(
      consoleWidth,
      startingPointObj[sub_model]
    );
    setRailDropBoxes(dropPoints);
  }, [width, sub_model]);

  //adding some extra pixels in outer div to handle scroll issue while for left and right chase
  const widthCaluclation = useMemo(() => {
    return Math.round(width.in / pixelPerInch) + 100;
  }, [width]);

  //chase width calculation
  const railWidth = useMemo(() => {
    const borderAreaMap = {
      surface_mounted: 7.125,
      enclosure: 6.25,
      recessed: 4.3756,
      insight: 5.375,
    };
    return (width.ft * 12 + width.in - borderAreaMap[sub_model]) / pixelPerInch;
  }, [width, sub_model]);

  const serviceWidth = (ser) => {
    // return ser.category === "Medical Gas Outlet"
    //   ? ser.gang_width / pixelPerInch
    //   : ser.width / pixelPerInch;
    return ser.width / pixelPerInch;
  };

  const servicePosition = (ser) => {
    return `translate(${ser.positions?.x}px, ${0}px)`;
  };

  const generateStyle = (ser) => {
    const styleObj = {
      cursor: "grab",
      background:
        ser === serv
          ? view_only
            ? "rgba(207, 35, 35, 0.2)"
            : "transparent"
          : "transparent",
    };
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor);
    if (isSafari) {
      styleObj.left = ser.positions.x;
      styleObj.top = ser?.is_slide ? "80px" : "0px";
    } else styleObj.transform = servicePosition(ser);
    return styleObj;
  };

  const wallHeight = useMemo(() => {
    return Math.round(
      (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch
    );
  }, [ceiling_height]);

  useEffect(() => {
    //to adjust scale for axiom
    // Select all elements with the class 'scale'
    if (ruler) {
      const firstElements = document.querySelector(".first");
      const nextElements = document.querySelectorAll(".next");

      // Update styles for 'first' elements
      firstElements.style.maxWidth = "220.285714285px";

      nextElements.forEach((element) => {
        element.style.maxWidth = "214.285714285px";
      });
      // Cleanup function to revert styles when component unmounts
      return () => {
        firstElements.style.maxWidth = "";
        nextElements.forEach((element) => {
          element.style.maxWidth = "";
        });
      };
    }
  }, [ruler, width]);

  const handleKeyDown = (e) => {
    switch (e.key) {
      case "ArrowUp":
        break;
      case "ArrowDown":
        break;
      case "ArrowLeft":
        view_only && clickArrowLeft(customEvent, serv, railInd, 0, "infinity");
        break;
      case "ArrowRight":
        view_only && clickArrowRight(customEvent, serv, railInd, 0, "infinity");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    let isDragging = false;

    const handleClickOutside = (e) => {
      const clickedInsideFlexItemsEnd = e.target.closest(
        ".flex.items-end".split(" ").join(".")
      );
      if (
        !isDragging &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target) &&
        clickedInsideFlexItemsEnd == null
      ) {
        setService("", false);
      }
    };

    const handleDragStart = () => (isDragging = true);
    const handleDragEnd = () => (isDragging = false);

    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside); // fixed
    document.addEventListener("dragstart", handleDragStart);
    document.addEventListener("dragend", handleDragEnd);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside); // match above
      document.removeEventListener("dragstart", handleDragStart);
      document.removeEventListener("dragend", handleDragEnd);
    };
  }, [serv, view_only]);

  const rulerWidth = useMemo(() => {
    let totalFeet = width.ft;
    let totalInches = width.in;
    if (
      sub_model === "surface_mounted" ||
      sub_model === "recessed" ||
      sub_model === "insight"
    ) {
      // Convert total inches to feet and inches
      totalFeet = Math.floor(totalInches / 12);
      totalInches = totalInches % 12;
    }

    return { ft: totalFeet, in: totalInches };
  }, [width, sub_model]);

  const lid_Width = useMemo(() => {
    //subtracting area which don't need lid
    const borderAreaMap = {
      surface_mounted: 4.875,
      enclosure: 4.25,
      recessed: 3.6256,
      insight: 1.625,
    };
    return (width.ft * 12 + width.in - borderAreaMap[sub_model]) / pixelPerInch;
  }, [width, sub_model]);

  const lidTop = useMemo(() => {
    const marginFromTop = {
      surface_mounted: "167px",
      enclosure: "167px",
      recessed: "140px",
      insight: headwalls.length > 1 ? "292px" : "185px",
    };
    return marginFromTop[sub_model];
  }, [sub_model, headwalls]);

  const getOccupiedDropIndexes = (rail) => {
    const occupied = new Set();
    const boxWidth = 40.1785714286;
    const serviceWidth = service?.service?.width / pixelPerInch;
    if (serviceWidth > 50) {
      //if image size is greater than approx 3 inch
      occupied.add(0);
      occupied.add(railDropBoxes.length - 1);
    }

    railDropBoxes.forEach((point, dropIndex) => {
      const dropPoint = point - 20.0892857143;
      rail.services.forEach((ser) => {
        const serviceWidth = ser.width / pixelPerInch;
        const serviceLeft = ser.positions.x;
        const serviceRight = serviceLeft + serviceWidth;

        const boxLeft = dropPoint;
        const boxRight = dropPoint + boxWidth;

        // --- Rule 1: Normal overlap check (same as your some(...) code)
        const overlaps = !(serviceRight <= boxLeft || serviceLeft >= boxRight);
        if (overlaps) {
          occupied.add(dropIndex);
        }

        // --- Rule 2: Medical Gas outlet → block neighbors
        if (ser.category === "Medical Gas Outlet" && overlaps) {
          occupied.add(dropIndex - 1);
          occupied.add(dropIndex + 1);
        }
      });
    });

    return occupied;
  };

  // util for lighter/darker shades
  const adjustColor = (hex, amount) => {
    if (!hex) return hex;
    let col = hex.startsWith("#") ? hex.slice(1) : hex;
    let num = parseInt(col, 16);

    let r = (num >> 16) + amount;
    let g = ((num >> 8) & 0x00ff) + amount;
    let b = (num & 0x0000ff) + amount;

    r = Math.max(Math.min(255, r), 0);
    g = Math.max(Math.min(255, g), 0);
    b = Math.max(Math.min(255, b), 0);

    return `rgb(${r}, ${g}, ${b})`;
  };

  return (
    <>
      <Wrapper
        ruler={ruler}
        width={widthCaluclation}
        wallHeight={wallHeight}
        wallLength={wallLength}
        railWidth={railWidth}
        ceiling_height={ceiling_height}
        model={model}
        ref={ref}
      >
        <div className={`h-full flex justify-center `}>
          <div className="h-full flex relative justify-center">
            <div
              className={`flex flex-col h-full relative justify-end w-full`}
              style={{
                width: (rulerWidth.ft * 12 + rulerWidth.in) / pixelPerInch,
                height:
                  (ceiling_height.ft * 12 + ceiling_height.in) / pixelPerInch,
              }}
            >
              <>
                <Fragment>
                  <div
                    className={`w-max ${outerClass[sub_model]} rail_center relative`}
                    onDragEnter={handleDragEnter}
                    style={{
                      background: headwalls[0].color,
                      borderColor: adjustColor(headwalls[0].color, -20),
                    }}
                  >
                    <div
                      className="security_lid"
                      style={{
                        background: headwalls[0].color,
                        width: lid_Width,
                        top: lidTop,
                        height:
                          sub_model === "insight" && headwalls.length > 1
                            ? "292px"
                            : "167px",
                      }}
                    >
                      <div
                        className="relative h-full flex justify-center"
                        style={{ borderTop: "2px dashed #022323" }}
                      >
                        {/* <div className=" absolute  "></div>
                          <div style={{borderRadius: "0  0 87% 0"}} className="w-[30px] h-[30px] bg-transparent absolute top-[-2px] left-0"></div> */}

                        <div
                          style={{
                            width: "100%",
                            height: "2px",
                            background: " #022323",
                          }}
                        />
                        {/* <div style={{borderRadius: "0 0 0 87%"}} className="w-[30px] h-[30px] bg-white absolute top-[-2px] right-0"></div> */}
                        {railWidth > 110 ? ( // greater than 6.16 inches
                          <>
                            {["left-4", "right-4"].map((position) => (
                              <img
                                draggable="false"
                                key={position}
                                src={InsightLockIcon}
                                alt="Security Lock"
                                className={`absolute bottom-4 ${position}`}
                              />
                            ))}
                          </>
                        ) : (
                          <img
                            draggable="false"
                            src={InsightLockIcon}
                            alt="aa"
                            className=" absolute bottom-4"
                          />
                        )}
                      </div>
                    </div>
                    <div
                      className={`${innerClass[sub_model]} flex flex-col gap-[26.7857142857px]`}
                      //  style={{borderColor:headwalls[0].color}}
                      style={{
                        background: adjustColor(headwalls[0].color, 20), // slightly lighter inner fill
                        borderColor: adjustColor(headwalls[0].color, -20), // lighter border for depth
                      }}
                    >
                      {headwalls?.map((rail, railIndex) => {
                        const occupiedIndexes = getOccupiedDropIndexes(rail);
                        return (
                          <div
                            key={railIndex}
                            className={`relative h-[80.3571428571px] flex w-full`}
                            style={{
                              backgroundColor: headwalls[0]?.color,
                              width: railWidth,
                              // backgroundImage: `url(${
                              //   GRAINS[rail?.background]
                              // })`,
                            }}
                            onDrop={(e) => handleDrop(e, "rail", railIndex)}
                            onDragOver={(e) => handleonDragOver(e, "rail")}
                          >
                            {/* Dropboxes */}
                            {railDropBoxes.map((point, dropIndex) => {
                              const dropPoint = point - 20.0892857143; // X position of drop point subtracting 20px to move it to left
                              const occupied = occupiedIndexes.has(dropIndex);
                              return (
                                <>
                                  {(dragEntered || serv) && (
                                    <div
                                      key={dropIndex}
                                      className={`absolute top-0 h-full transition-all duration-200 ${
                                        occupied ? "" : "border border-blue-500"
                                      }`}
                                      style={{
                                        left: dropPoint,
                                        width: "40.1785714286px",
                                      }}
                                      onDragEnter={(e) => e.stopPropagation()}
                                    />
                                  )}
                                </>
                              );
                            })}
                            {rail.services.map((ser, serviceIndex) => (
                              <Fragment key={serviceIndex}>
                                <div
                                  draggable={true}
                                  draggableid={ser._id}
                                  className={`service_main absolute py-1 flex justify-center h-full items-center ${
                                    dragEntered &&
                                    !service.service.is_accessory_rail &&
                                    !service.service.is_accessory
                                      ? "non_dropable "
                                      : ""
                                  }`}
                                  style={generateStyle(ser)}
                                  onDragStart={(e) =>
                                    handleDragStart(
                                      ser,
                                      e,
                                      railIndex,
                                      serviceIndex
                                    )
                                  }
                                  onDragEnter={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    setCustomEvent(e);
                                    setService(ser, true);
                                    setRailInd(railIndex);
                                  }}
                                >
                                  <div
                                    draggable={"false"}
                                    className="service_img pointer-events-none"
                                    style={{
                                      width: serviceWidth(ser),
                                      backgroundImage: `url(${alterImageUrl(
                                        ser.image_url
                                      )})`,
                                      backgroundSize: "contain",
                                      backgroundRepeat: "no-repeat",
                                      backgroundPosition: "center",
                                    }}
                                    onDragEnter={(e) => e.stopPropagation()}
                                  />
                                </div>
                              </Fragment>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </Fragment>

                {ruler && (
                  <HorizontalRuler
                    class_name={"w-full bottom-[14px] z-50"}
                    width={rulerWidth}
                  />
                )}
              </>
            </div>
          </div>
        </div>
        {showBedGraphic && <HospitalBed />}
      </Wrapper>
    </>
  );
});

export default SecurityConsolePanel;
